import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { FETCH_OPTIMIZER_AUDITS_V2 } from '../queries/optimizerAuditsV2.queries';
import type {
  OptimizerAuditRollup,
  OptimizerAuditV2Record,
} from '../types/audit.types';
import type { BulkRebalanceRun } from '../types/run.types';
import { aggregateOptimizerAudits } from '../utils/audit/aggregateOptimizerAudits';
import { OPTIMIZER_POLL_INTERVAL_MS } from '../utils/detailPage/constants';
import { canLoadRunAudits } from '../utils/presentation/runProgress';

export type UseBulkRebalanceDetailAuditsResult = {
  auditRecords: OptimizerAuditV2Record[];
  auditLoading: boolean;
  auditRollup: OptimizerAuditRollup | null;
};

export const useBulkRebalanceDetailAudits = (
  runId: string | undefined,
  run: BulkRebalanceRun | null,
  inProgress: boolean,
  canReadPortfolioOptimizer: boolean,
): UseBulkRebalanceDetailAuditsResult => {
  const showAuditSummary =
    !!runId &&
    !!run &&
    canReadPortfolioOptimizer &&
    canLoadRunAudits(run.state);

  const { loading: auditLoading, data: auditData } = useQuery(
    FETCH_OPTIMIZER_AUDITS_V2,
    {
      skip: !showAuditSummary,
      fetchPolicy: 'no-cache',
      pollInterval:
        inProgress && run && canLoadRunAudits(run.state)
          ? OPTIMIZER_POLL_INTERVAL_MS
          : 0,
      variables: {
        input: {
          optimizerRunId: runId,
        },
      },
    },
  );

  const auditRecords: OptimizerAuditV2Record[] =
    auditData?.fetchOptimizerAuditsV2?.audits ?? [];

  const auditRollup = useMemo(() => {
    if (auditRecords.length === 0) {
      return null;
    }

    return aggregateOptimizerAudits(auditRecords);
  }, [auditRecords]);

  return {
    auditRecords,
    auditLoading,
    auditRollup,
  };
};
